import {
  BoxShadow,
  CssVariable,
  LengthEvaluator,
  Color,
} from '@wix/css-property-parser';

type BoxShadowParsedValue = {
  offsetX: string | null;
  offsetY: string | null;
  blurRadius: string | null;
  spreadRadius: string | null;
  color: string | null;
  inset: string | null;
};

const parseBoxShadowValue = (value: string) => {
  const boxShadow = BoxShadow.parse(value);

  const values: BoxShadowParsedValue = {
    blurRadius: null,
    color: null,
    offsetX: null,
    offsetY: null,
    spreadRadius: null,
    inset: null,
  };

  if (!boxShadow) {
    return values;
  }

  if ('type' in boxShadow && boxShadow.type === 'variable') {
    Object.keys(values).forEach((key) => {
      values[key as keyof BoxShadowParsedValue] =
        CssVariable.toCSSValue(boxShadow);
    });

    return values;
  }

  if ('shadows' in boxShadow && boxShadow.shadows && boxShadow.shadows.length) {
    const shadow = boxShadow.shadows[0];

    if ('offsetX' in shadow && shadow.offsetX) {
      values.offsetX = LengthEvaluator.toCSSValue(shadow.offsetX);
    }
    if ('offsetY' in shadow && shadow.offsetY) {
      values.offsetY = LengthEvaluator.toCSSValue(shadow.offsetY);
    }

    if ('blurRadius' in shadow && shadow.blurRadius) {
      values.blurRadius = LengthEvaluator.toCSSValue(shadow.blurRadius);
    }

    if ('spreadRadius' in shadow && shadow.spreadRadius) {
      values.spreadRadius = LengthEvaluator.toCSSValue(shadow.spreadRadius);
    }

    if ('color' in shadow && shadow.color) {
      values.color = Color.toCSSValue(shadow.color);
    }

    if ('inset' in shadow && shadow.inset) {
      values.inset = shadow.inset ? 'inset' : null;
    }
  }

  return values;
};

const boxShadowParsedValueToString = (
  parsedValue: BoxShadowParsedValue,
): string => {
  const { offsetX, offsetY, blurRadius, spreadRadius, color, inset } =
    parsedValue;

  // If all values are null, return 'none'
  if (
    !offsetX &&
    !offsetY &&
    !blurRadius &&
    !spreadRadius &&
    !color &&
    !inset
  ) {
    return 'none';
  }

  const x = offsetX || '0px';
  const y = offsetY || '0px';

  const parts: string[] = [];

  if (inset) {
    parts.push('inset');
  }

  parts.push(x, y);

  if (blurRadius) {
    parts.push(blurRadius);
  }

  if (spreadRadius) {
    parts.push(spreadRadius);
  }

  if (color) {
    parts.push(color);
  }

  return parts.join(' ');
};

export {
  parseBoxShadowValue,
  boxShadowParsedValueToString,
  BoxShadowParsedValue,
};
